import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import ConfirmDialog from '../../../components/ConfirmDialog';
import LabelCheckbox from '../../../components/LabelCheckbox';
import colors from '../../../../styles/colors';
import { substituteForPreview } from '../utils/composeBodyPreview';
import { formatRecipientCount } from '../utils/formatRecipientCount';
import { EM_DASH } from '../utils/constants';
import texts from '../texts.json';

const Row = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 13, py: 0.5 }}>
    <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
    <Typography variant="caption">{value}</Typography>
  </Stack>
);

Row.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };
Row.defaultProps = { value: EM_DASH };

const ImagePreviewRow = ({ url }) => (
  <Box
    component="img"
    src={url}
    alt={texts.confirmModal.imageAlt}
    sx={{
      mt: 1.5,
      width: '100%',
      maxHeight: 140,
      objectFit: 'cover',
      borderRadius: 1,
    }}
  />
);

ImagePreviewRow.propTypes = { url: PropTypes.string.isRequired };

const ConfirmSendDialog = ({
  isOpen,
  isSubmitting,
  loadingMessage,
  coachName,
  audienceLabel,
  showConsentWarning,
  recipientCount,
  isCapped,
  dplName,
  imageUrl,
  body,
  onConfirm,
  onCancel,
}) => {
  const [consentTicked, setConsentTicked] = useState(false);

  useEffect(() => {
    if (isOpen) setConsentTicked(false);
  }, [isOpen]);

  const loadingContent = (
    <Box sx={{
      minWidth: 360,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      py: 4,
    }}
    >
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary">
        {loadingMessage}
      </Typography>
    </Box>
  );

  const summaryContent = (
    <Box sx={{ minWidth: 360 }}>
      <Row label={texts.confirmModal.summary.coach} value={coachName || EM_DASH} />
      <Row label={texts.confirmModal.summary.audience} value={audienceLabel || EM_DASH} />
      <Row
        label={texts.confirmModal.summary.recipients}
        value={formatRecipientCount(recipientCount, isCapped)}
      />
      <Row label={texts.confirmModal.summary.dpl} value={dplName || EM_DASH} />
      {imageUrl && <ImagePreviewRow url={imageUrl} />}
      <Box sx={{
        mt: 1.5,
        p: 1.25,
        borderRadius: 1,
        bgcolor: colors.base.gamma,
        whiteSpace: 'pre-wrap',
        fontSize: 13,
      }}
      >
        {substituteForPreview(body, { coachName })}
      </Box>
      {showConsentWarning && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {texts.confirmModal.consentWarning}
        </Alert>
      )}
      <Box sx={{ mt: 1 }}>
        <LabelCheckbox
          isChecked={consentTicked}
          onChange={setConsentTicked}
          description={texts.confirmModal.consent}
        />
      </Box>
    </Box>
  );

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onConfirm={onConfirm}
      onCancel={isSubmitting ? null : onCancel}
      confirmDisabled={!consentTicked || isSubmitting}
      dialogTexts={{
        title: texts.confirmModal.title,
        confirm: texts.confirmModal.confirm,
        cancel: texts.confirmModal.cancel,
        content: isSubmitting ? loadingContent : summaryContent,
      }}
    />
  );
};

ConfirmSendDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  loadingMessage: PropTypes.string,
  coachName: PropTypes.string,
  audienceLabel: PropTypes.string,
  showConsentWarning: PropTypes.bool,
  recipientCount: PropTypes.number.isRequired,
  isCapped: PropTypes.bool,
  dplName: PropTypes.string,
  imageUrl: PropTypes.string,
  body: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmSendDialog.defaultProps = {
  isSubmitting: false,
  loadingMessage: '',
  coachName: '',
  audienceLabel: '',
  showConsentWarning: false,
  isCapped: false,
  dplName: '',
  imageUrl: '',
};

export default ConfirmSendDialog;
